import { SHELL_TARGETS } from "../../spotlight/targets";
import { validators } from "../validation";
import type { StepDefinition } from "./StepDefinition";
import type { EditorStepId } from "../../persistence/schema";
import type { StepContext } from "../types";

/**
 * Editor onboarding step registry (schema v2).
 *
 * Blank pages now follow the boutique-lodge homepage structure block by
 * block, then move through save → history → preview → SEO → publish.
 * Template pages auto-bypass the already-satisfied build steps.
 */

const BLANK_BUILD_STEPS = [
  {
    id: "e1_navbar",
    type: "Navbar",
    title: "Start with the navbar",
    body: "Start with a clear top bar. It helps guests orient themselves quickly and makes the page feel trustworthy from the first glance.",
    hint: "Add a logo, a few important links, and one clear action.",
  },
  {
    id: "e2_hero",
    type: "Hero",
    title: "Add the hero",
    body: "Shape the first impression. This section should quickly tell guests what makes the stay worth exploring.",
    hint: "A strong image, a clear headline, and one inviting action go a long way.",
  },
  {
    id: "e3_accommodation_grid",
    type: "AccommodationGrid",
    title: "List the rooms and cabins",
    body: "Help guests picture their stay. Add your rooms or cabins early so visitors can quickly understand their options.",
    hint: "This is the template's main stay section.",
  },
  {
    id: "e4_amenities_grid",
    type: "AmenitiesGrid",
    title: "Show what's included",
    body: "Now reassure guests with the details that make a stay feel easy and comfortable.",
    hint: "Things like breakfast, Wi-Fi, parking, or wellness perks help reduce hesitation.",
  },
  {
    id: "e5_gallery_section",
    type: "GallerySection",
    title: "Add the gallery",
    body: "Next add Gallery so visitors can feel the rooms, grounds, and atmosphere before they inquire.",
    hint: "This section does a lot of emotional selling.",
  },
  {
    id: "e6_host_story",
    type: "HostStory",
    title: "Make the hosts feel real",
    body: "Add a human touch. A short host story makes the experience feel more personal and trustworthy.",
    hint: "A short founder or host story is enough.",
  },
  {
    id: "e7_offer_cards",
    type: "OfferCards",
    title: "Add the offers section",
    body: "Now place Offers to highlight direct-booking perks, packages, or seasonal stays.",
    hint: "This is where you give visitors a reason to book direct.",
  },
  {
    id: "e8_destination_guide",
    type: "DestinationGuide",
    title: "Sell the destination too",
    body: "Add Local guide next. Great hospitality pages sell the area just as much as the room.",
    hint: "Trails, beaches, restaurants, and local favourites all work well here.",
  },
  {
    id: "e9_inquiry_band",
    type: "InquiryBand",
    title: "Add the inquiry band",
    body: "End with a clear next step so guests never wonder what to do next.",
    hint: "This is your strongest direct-contact prompt.",
  },
  {
    id: "e10_footer",
    type: "Footer",
    title: "Finish with the footer",
    body: "Add Footer last to close the page with navigation, policies, and contact details.",
    hint: "Once the footer is in, the homepage skeleton is complete.",
  },
] as const satisfies ReadonlyArray<{
  id: EditorStepId;
  type: string;
  title: string;
  body: string;
  hint: string;
}>;

function closePanels(ctx: StepContext): void {
  if (ctx.showSeoPanel) ctx.setShowSeoPanel(false);
  if (ctx.showVersions) ctx.setShowVersions(false);
}

function closeSeo(ctx: StepContext): void {
  if (ctx.showSeoPanel) ctx.setShowSeoPanel(false);
}

function openHistory(ctx: StepContext): void {
  if (ctx.showSeoPanel) ctx.setShowSeoPanel(false);
  if (!ctx.showVersions) ctx.setShowVersions(true);
}

function openSeo(ctx: StepContext): void {
  if (ctx.showVersions) ctx.setShowVersions(false);
  if (!ctx.showSeoPanel) ctx.setShowSeoPanel(true);
}

const ORDERED_BUILD_STEPS: StepDefinition[] = BLANK_BUILD_STEPS.map(
  (step, index) => ({
    id: step.id,
    tier: "soft",
    target: `palette:item:${step.type}`,
    placement: "right-of-target",
    onEnter: closePanels,
    copy: () => ({
      title: step.title,
      body: step.body,
      primaryLabel: "I added it",
      hint: step.hint,
    }),
    autoPass: (ctx) =>
      validators.blankBuildSequenceSatisfied(ctx.data, index + 1),
    validate: (ctx) =>
      validators.blankBuildSequenceSatisfied(ctx.data, index + 1),
  }),
);

export const STEPS: StepDefinition[] = [
  {
    id: "e0_welcome",
    tier: "hard",
    target: null,
    placement: "modal-center",
    copy: (ctx) => ({
      title: "Build This Page With Me",
      body:
        ctx.branch === "template"
          ? "This page already has its structure. I'll walk you through the professional finish: save it, review history, preview it, fill the SEO fields, and publish it."
          : "Let’s build a page guests would trust. We’ll shape it section by section, refine the details, and get it ready to preview and publish — together.",
      primaryLabel: "Start walkthrough",
      secondaryLabel: "Skip for now",
    }),
  },
  ...ORDERED_BUILD_STEPS,
  {
    id: "e11_save",
    tier: "soft",
    target: SHELL_TARGETS.SAVE_BUTTON,
    placement: "below-target",
    onEnter: closePanels,
    copy: () => ({
      title: "Save this draft",
      body: "Save your progress so everything is safely stored before we preview or publish.",
      primaryLabel: "Save page",
      hint: "Once saved, you’ll always have a version to return to.",
    }),
    autoPass: (ctx) => !ctx.isDirty,
    validate: (ctx) => !ctx.isDirty,
    primaryAction: (ctx) => {
      ctx.invokeSave();
    },
  },
  {
    id: "e12_history_button",
    tier: "soft",
    target: SHELL_TARGETS.HISTORY_BUTTON,
    placement: "below-target",
    onEnter: closeSeo,
    copy: () => ({
      title: "Open History next",
      body: "History is where every saved version lives. Open it from the top bar, or use the button on this card, so you can see the restore points created by your saves.",
      primaryLabel: "Open history",
    }),
    autoPass: (ctx) => ctx.showVersions || ctx.historyOpenedCount > 0,
    validate: (ctx) => ctx.showVersions || ctx.historyOpenedCount > 0,
    primaryAction: (ctx) => {
      openHistory(ctx);
    },
  },
  {
    id: "e13_history_panel",
    tier: "soft",
    target: SHELL_TARGETS.VERSIONS_PANEL_ROOT,
    placement: "left-of-target",
    onEnter: openHistory,
    copy: () => ({
      title: "You can always go back",
      body: "Every saved version is kept here, so experimenting feels safe. If you change your mind later, restoring an earlier version is easy.",
      primaryLabel: "Continue",
      hint: "You don't need to restore anything now — just know this panel exists.",
    }),
  },
  {
    id: "e14_preview",
    tier: "soft",
    target: SHELL_TARGETS.PREVIEW_BUTTON,
    placement: "below-target",
    onEnter: closePanels,
    copy: () => ({
      title: "Preview the page as a guest",
      body: "See the page the way a guest would. Take one quick pass from top to bottom and ask: is it clear, trustworthy, and easy to act on?",
      primaryLabel: "Open preview",
      hint: "Try reading it quickly — good pages make sense fast.",
    }),
    autoPass: (ctx) => ctx.previewOpenedCount > 0,
    validate: (ctx) => ctx.previewOpenedCount > 0,
    primaryAction: (ctx) => {
      ctx.invokePreview();
    },
  },
  {
    id: "e15_seo",
    tier: "soft",
    target: SHELL_TARGETS.SEO_PANEL_ROOT,
    placement: "left-of-target",
    onEnter: openSeo,
    copy: () => ({
      title: "Help search and sharing tell the right story",
      body: "This is where you shape how the page appears in search results and when shared. Start with a clear title and short description.",
      primaryLabel: "Continue",
      hint: "You can refine the advanced settings any time.",
    }),
  },
  {
    id: "e16_publish",
    tier: "soft",
    target: SHELL_TARGETS.PUBLISH_BUTTON,
    placement: "below-target",
    onEnter: closePanels,
    copy: (ctx) =>
      ctx.canPublish
        ? {
            title: ctx.publishError
              ? "Publish didn't go through"
              : "Publish the finished page",
            body: ctx.publishError
              ? "Something interrupted the publish. Check your connection and try again — your draft is safe."
              : "You’re ready. If the page feels clear, trustworthy, and ready to share, publish it.",
            primaryLabel: ctx.pagePublished
              ? "Already live"
              : ctx.publishError
                ? "Try again"
                : "Publish now",
            hint: ctx.publishError
              ? "If it keeps failing, refresh and reopen the page — nothing is lost."
              : "You can always come back and improve it later.",
          }
        : {
            title: "Ask an admin to publish",
            body: "Your role can edit but not publish. Copy the page link and send it to a workspace admin to take it live.",
            primaryLabel: "Copy preview link",
            secondaryLabel: "Skip step",
          },
    autoPass: (ctx) => ctx.canPublish && ctx.pagePublished === true,
    validate: (ctx) => ctx.pagePublished === true,
    primaryAction: (ctx) => {
      if (ctx.canPublish) {
        ctx.invokePublish();
        return;
      }
      const href = typeof window !== "undefined" ? window.location.href : "";
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard) {
          void navigator.clipboard.writeText(href);
        }
      } catch {
        // ignore — clipboard is a nicety, not a contract
      }
    },
  },
  {
    id: "e17_post_publish",
    tier: "soft",
    target: null,
    placement: "modal-center",
    copy: (ctx) => ({
      title: ctx.pagePublished ? "Your page is live" : "Walkthrough finished",
      body: ctx.pagePublished
        ? "Your page is live. You can keep refining it any time — great pages improve over time."
        : "You can re-open the walkthrough any time from your Profile page.",
      primaryLabel: ctx.pagePublished ? "View live page" : "Done",
      secondaryLabel: "Create another page",
    }),
    primaryAction: (ctx) => {
      if (ctx.pagePublished) ctx.invokePreview();
    },
    secondaryAction: (ctx) => {
      ctx.navigateToNewPage();
    },
  },
];

export const STEP_BY_ID: Record<EditorStepId, StepDefinition | undefined> =
  STEPS.reduce<Record<string, StepDefinition>>((acc, step) => {
    acc[step.id] = step;
    return acc;
  }, {}) as Record<EditorStepId, StepDefinition | undefined>;

export const STEP_ORDER: ReadonlyArray<StepDefinition["id"]> = STEPS.map(
  (s) => s.id,
);

// Back-compat aliases. Phase 5 callers still reference these names.
export const PHASE_5_STEPS = STEPS;
export const PHASE_5_ORDER = STEP_ORDER;

export function nextStepId(
  current: StepDefinition["id"],
): StepDefinition["id"] | null {
  const idx = STEP_ORDER.indexOf(current);
  if (idx === -1) return null;
  const next = STEP_ORDER[idx + 1];
  return next ?? null;
}

// Dev-only invariant: every step id must be unique. Catches stale
// aliases / typos at module load, before they explode inside the
// runner. Uses Vite's `import.meta.env.DEV` so it tree-shakes out of
// production bundles.
if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
  const ids = new Set(STEPS.map((s) => s.id));
  if (ids.size !== STEPS.length) {
    // eslint-disable-next-line no-console
    console.warn(
      "[onboarding] duplicate step id detected in registry",
      STEPS.map((s) => s.id),
    );
  }
}
